import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { classMap } from 'lit/directives/class-map.js';
import {
  customElement,
  property,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import componentStyles from './se-feature-grid.css.lit';
import { SlotController } from '../../controllers/slot-controller';

/**
 * The SeFeatureGrid component
 * @element se-feature-grid
 * @slot main - for main content.
 * @slot cta - for use with up to two cta links.
 * @slot heading - for a se-heading component.
 * @slot featured - for featured se-insight-card.
 */
@customElement('se-feature-grid')
export class SeFeatureGrid extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(this, false);

  /**
   * Ref to the slot featured
   */
  @queryAssignedElements({ slot: 'featured', flatten: true })
  slotFeatured: Array<HTMLElement>;
  /**
   * Ref to the slot cta
   */
  @queryAssignedElements({ slot: 'cta', flatten: true })
  slotCta: Array<HTMLElement>;

  /**
   * Ref to the slot heading
   */
  @queryAssignedElements({ slot: 'heading', flatten: true })
  slotHeading: Array<HTMLElement>;

  /**
   * Ref to the slot description
   */
  @queryAssignedElements({ slot: 'description', flatten: true })
  slotDescription: Array<HTMLElement>;

  /**
   * Ref to the slot main
   */
  @queryAssignedElements({ slot: 'main', flatten: true })
  slotMain: Array<HTMLElement>;

  /**
   * If heading or cta slots have content.
   */
  @property({ type: Boolean })
  hasTopContent: boolean;

  /**
   * if featured slot has content.
   */
  @property({ type: Boolean })
  hasFeatured: boolean;

  /**
   * if featured slot has description content.
   */
  @property({ type: Boolean })
  hasDescription: boolean;

  /**
   * If accordions are slotted into main slot.
   * Sets class to remove row-gap for stacking on mobile.
   */
  @property({ type: Boolean })
  hasAccordions: boolean;

  /**
   * If only one item slotted into main slot.
   * Sets class to remove 2 column layout on desktop.
   */
  @property({ type: Boolean, attribute: 'is-single' })
  isSingle: boolean;

  @state() hasBottomSlot: boolean;

  firstUpdated(): void {
    this.checkSlots();
  }

  render(): TemplateResult {
    const topClasses = {
      'feature-grid--top-content': true,
      'hidden': !this.hasTopContent,
      'accordions': this.hasAccordions,
      'bottom': this.hasTopContent && !this.hasFeatured,
      'reduce-bottom': this.hasTopContent && !this.hasFeatured && this.hasDescription,
    };
    const descriptionClasses = {
      'feature-grid--top-content-description': true,
      'bottom': this.hasTopContent && !this.hasFeatured && this.hasDescription,
    };
    const featuredClasses = {
      'feature-grid--featured': true,
      'hidden': !this.hasFeatured,
      'accordions': this.hasAccordions,
      'bottom': this.hasFeatured,
    };
    const mainClasses = {
      'feature-grid--columns': true,
      'accordions': this.hasAccordions,
      'single': this.isSingle,
    };
    return html`
      <div class="feature-grid">
        <div class="${classMap(topClasses)}">
          <div class="feature-grid--top-content-heading">
            <slot name="heading"></slot>
          </div>
        </div>
        <div class="${classMap(descriptionClasses)}">
          <div class="feature-grid--top-content-description">
            <slot name="description"></slot>
          </div>
          <div class="feature-grid--top-content-cta">
            <slot name="cta"></slot>
          </div>
        </div>
        <div class="${classMap(featuredClasses)}">
          <slot name="featured"></slot>
        </div>
        <div class="${classMap(mainClasses)}">
          <slot name="main"></slot>
        </div>
        ${this.hasBottomSlot
          ? html` <div class="feature-grid--bottom-content">
              <slot name="bottom"></slot>
            </div>`
          : ''}
      </div>
    `;
  }

  checkSlots() {
    this.hasTopContent = !!this.slotHeading.length || !!this.slotCta.length;
    this.hasFeatured = !!this.slotFeatured.length;
    this.hasDescription = !!this.slotDescription.length;
    this.hasAccordions = !!this.slotMain.filter(
      el => el.localName === 'se-accordion'
    ).length;
    this.isSingle = this.isSingle || this.slotMain.length < 2;
  }  
}
